import React from "react";
import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import {
  ArrowUp, CalendarDays, Calendar, Clock, Tag, CheckSquare, CheckCircle,
  ListTree, Circle, Flag, X, Check,
} from "lucide-react";
import Modal from "../../components/Modal";
import { CategoryIcon } from "../../components/CategoryIcon";
import { categoryInk } from "../../theme/colorEngine";
import { PRIORITY_COLORS, TASK_PRIORITIES } from "../../constants/palettes";
import { pushBackHandler } from "../../capacitor/backButton";
import { setNativeAutofill } from "../../capacitor/clarityBars";
import { showKeyboardNow } from "../../capacitor/keyboardNow";
import { useBrowserKeyboardInset } from "../../capacitor/keyboardInset";
import { todayStr } from "../../utils/dateHelpers";
import {
  splitPastedLines, quickDateLabel,
} from "../../utils/quickAdd";
import { buildItemPayload } from "../../utils/itemPayload";
import { uid } from "../../utils/storage";
import { ItemPanel } from "./CalendarExtras";
import { GRAD, MonthPicker, TimeRow, EndTimeRow, AllDayRow } from "./DatePickParts";

// ─── PLANNER QUICK-ADD BAR (Jade, 2026-10-09) ─────────────────────────────────
// A full-width bar that sits on top of the keyboard, opened by the Planner's
// "+" buttons on mobile (and the Planner widget's +). Title + Send on top; icon
// buttons for Date, Category, Priority and Subtasks, the Task/Event switch and
// "More" below.
// - Date opens a panel (calendar, quick picks, start/due date for tasks, and the
//   Time row); Category and Priority open a row of pills above the buttons.
//   Only one of those is open at a time — tapping any other button closes it.
// - Subtasks add rows under the title (scrolling past five).
// - Send / Enter adds the item and keeps the bar open for the next one; pasting
//   several lines offers one item per line.
// - "More" swaps the bar for the full form (ItemPanel), carrying over everything.
// Items are built with buildItemPayload, the same builder the full form uses.
//
// Keyboard: on Android the viewport already ends at the keyboard's top edge
// (SystemBars insetsHandling "css" pads it — see capacitor.config.ts and
// JournalTab's kbOffset), so bottom: 0 sits on the keyboard. In a phone browser
// the keyboard overlays the page instead, so we lift by the visualViewport gap.

const isNative = Capacitor.isNativePlatform();


// Buttons that shouldn't take focus from the title (so tapping them doesn't
// drop the keyboard).
const keepFocus = e => e.preventDefault();

// The app's global input:focus glow (App.jsx) drew a box inside the title pill
// and around each subtask row (Jade's screenshot). input.qa-plain outranks it.
const QA_CSS = "input.qa-plain:focus { box-shadow: none !important; border-color: transparent !important; outline: none !important; }";

// Pill (date quick picks, paste prompt). A real border, not a box-shadow ring,
// so a scrolling parent can never clip it.
const pill = (selected, ringColor) => ({
  flexShrink: 0, boxSizing: "border-box",
  display: "flex", alignItems: "center", gap: 5,
  height: 34, padding: "0 12px", borderRadius: 17,
  border: `1.5px solid ${selected ? (ringColor || "var(--c-primary)") : "var(--c-border)"}`,
  background: "transparent", fontFamily: "inherit", fontSize: 13, fontWeight: 700,
  color: "var(--c-text-soft)", cursor: "pointer", whiteSpace: "nowrap",
});

// Icon-only buttons in the bottom row.
const iconBtn = (active) => ({
  position: "relative", flexShrink: 0,
  width: 38, height: 38, borderRadius: 12, padding: 0,
  display: "flex", alignItems: "center", justifyContent: "center",
  border: "none", fontFamily: "inherit",
  background: active ? "var(--c-bg-active)" : "transparent",
  color: active ? "var(--c-text-accent)" : "var(--c-text-soft)",
  cursor: "pointer", whiteSpace: "nowrap",
});

const textBtn = {
  display: "flex", alignItems: "center", gap: 4, flexShrink: 0,
  height: 34, padding: "0 8px", borderRadius: 17, border: "none", background: "transparent",
  fontFamily: "inherit", fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
};

const HOLD_MS = 900;   // how long the bar is held still after a send
const SUB_ROW_H = 40; // one subtask row; the list scrolls past five
const MENU_ROW_H = 44; // one Category / Priority row
const MENU_ROWS = 8;   // rows shown before the menu scrolls (Jade: at least 8)

// The Category / Priority list card. Sits just above the bar, left-aligned.
function ChoiceMenu({ label, children }) {
  return (
    <div role="listbox" aria-label={label}
      onMouseDown={keepFocus}
      style={{
        position: "absolute", left: 12, bottom: "calc(100% + 8px)",
        // As wide as its longest row (Jade: fit the text), never wider than the screen.
        width: "max-content", minWidth: 150, maxWidth: "calc(100% - 24px)",
        maxHeight: MENU_ROWS * MENU_ROW_H + 12, overflowY: "auto", overscrollBehavior: "contain",
        background: "var(--c-bg-card)", borderRadius: 18, padding: "6px 0",
        boxShadow: "0 8px 32px rgba(60,30,50,0.22)", boxSizing: "border-box",
      }}>
      {children}
    </div>
  );
}

function MenuRow({ icon, label, selected, onPick }) {
  return (
    <button role="option" aria-selected={selected} onMouseDown={keepFocus} onClick={onPick}
      style={{
        width: "100%", height: MENU_ROW_H, display: "flex", alignItems: "center", gap: 10,
        padding: "0 14px", border: "none", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
        background: selected ? "var(--c-bg-active)" : "transparent",
        color: "var(--c-text)", fontSize: 15, fontWeight: selected ? 700 : 600,
      }}>
      <span style={{ width: 22, display: "flex", justifyContent: "center", flexShrink: 0, color: "var(--c-text-soft)" }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ width: 17, flexShrink: 0, display: "flex" }}>
        {selected && <Check size={17} strokeWidth={2.6} color="var(--c-text-accent)" />}
      </span>
    </button>
  );
}

// ── Date panel ────────────────────────────────────────────────────────────────
// Round 5 (Jade: "cleaner"): no outlined boxes — a soft segmented Start / Due
// switch (tasks only), a plain month grid, borderless quick picks, then Time.
// The end time is optional and only appears once picked ("+ End time"), and the
// phone picker's Clear removes it again. Tasks (Jade, 2026-10-10): the Start tab
// shows "Start time" + the optional end time; the Due tab shows only "Due time",
// the due date's own time (a deadline has no end, and the start time doesn't
// apply to it). Picking a due time with no due date makes the start day the
// due day ("due tonight, 11:59 PM"). Works on a draft; Done applies it,
// Cancel or a tap outside drops it. The bar hides while this is open.
function DatePanel({ type, initial, weekStart, onDone, onCancel }) {
  const [start, setStart] = React.useState(initial.date);
  const [due, setDue] = React.useState(initial.dueDate || "");
  const [time, setTime] = React.useState(initial.time || "");
  const [endTime, setEndTime] = React.useState(initial.time ? (initial.endTime || "") : "");
  const [dueTime, setDueTime] = React.useState(initial.dueDate ? (initial.dueTime || "") : "");
  const [editing, setEditing] = React.useState("start"); // "start" | "due" (tasks only)
  const isTask = type === "task";
  // "All day" switch: null = the type's default (events on, tasks off).
  const [allDayPick, setAllDayPick] = React.useState(initial.allDay ?? null);
  const allDay = allDayPick ?? (!isTask && !time);

  const pick = (d) => {
    if (isTask && editing === "due") {
      if (d < start) setStart(d); // due before the start: the start moves back to it
      setDue(d); // the start day itself is fine: "due today, 11:59 PM"
    } else {
      setStart(d);
      if (due && due < d) { setDue(""); setDueTime(""); }
    }
  };
  const setStartTime = (v) => { setTime(v); if (!v) setEndTime(""); else if (endTime && endTime <= v) setEndTime(""); };

  const segment = (key, label, value) => {
    const on = editing === key;
    return (
      <button onClick={() => setEditing(key)}
        style={{ flex: 1, minWidth: 0, border: "none", borderRadius: 12, padding: "7px 10px", cursor: "pointer", fontFamily: "inherit", textAlign: "left",
          background: on ? "var(--c-bg-card)" : "transparent", boxShadow: on ? "0 1px 4px rgba(60,30,50,0.12)" : "none", transition: "background 0.15s" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--c-text-soft)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: value ? (on ? "var(--c-text-accent)" : "var(--c-text)") : "var(--c-text-soft)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {value ? quickDateLabel(value) : "None"}
        </div>
      </button>
    );
  };

  return (
    <div onClick={onCancel} style={{ position: "absolute", inset: 0, zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Date and time"
        style={{ width: "min(380px, 100%)", maxHeight: "calc(100% - 16px)", overflowY: "auto", background: "var(--c-bg-card)", borderRadius: 24, boxShadow: "0 12px 44px rgba(60,30,50,0.22)", padding: "16px 16px 14px", boxSizing: "border-box" }}>

        {/* Tasks: which date the calendar sets */}
        {isTask && (
          <div style={{ display: "flex", gap: 4, padding: 4, borderRadius: 15, background: "var(--c-bg-active)", marginBottom: 14, position: "relative" }}>
            {segment("start", "Start", start)}
            {segment("due", "Due (optional)", due)}
            {due && (
              <button aria-label="Clear due date" onClick={() => { setDue(""); setDueTime(""); setEditing("start"); }}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "var(--c-text-soft)", padding: 6, cursor: "pointer", display: "flex" }}>
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Month calendar + quick picks (shared with the full form — DatePickParts.jsx) */}
        <MonthPicker start={start} due={due} isTask={isTask} editingDue={isTask && editing === "due"} weekStart={weekStart} onPick={pick} />

        {/* Time (+ optional end time) — or, on a task's Due tab, the due time only */}
        <div style={{ marginTop: 14, paddingTop: 8, borderTop: "1px solid var(--c-border)" }}>
          {isTask && editing === "due" ? (
            <TimeRow label="Due time" value={dueTime} onChange={v => { setDueTime(v); if (v && !due) setDue(start); }} />
          ) : (<>
            <AllDayRow on={allDay} onChange={on => { setAllDayPick(on); if (on) setStartTime(""); }} />
            {!allDay && <TimeRow label={isTask ? "Start time" : "Time"} value={time} onChange={setStartTime} />}
            {!allDay && time && <EndTimeRow start={time} value={endTime} onChange={setEndTime} />}
          </>)}
        </div>

        {/* Cancel / Done */}
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, marginTop: 10 }}>
          <button onClick={onCancel} style={{ ...textBtn, color: "var(--c-text-soft)", padding: "0 14px" }}>Cancel</button>
          <button onClick={() => onDone({ date: start, dueDate: isTask ? due : "", dueTime: isTask && due ? dueTime : "", time: allDay ? "" : time, endTime: !allDay && time ? endTime : "", allDay: allDayPick })}
            style={{ ...textBtn, color: "var(--c-on-primary)", background: GRAD, padding: "0 22px", height: 38, borderRadius: 19 }}>Done</button>
        </div>
      </div>
    </div>
  );
}

export function QuickAddBar({
  defaultDate, defaultType = "task",
  categories = [], setCategories, savedColors, addSavedColor,
  weekStart = "sunday",
  onAdd,    // (type, payload) => void
  onClose,
  portal,   // the tab's createPortal (so the bar hides with the tab)
}) {
  const [phase, setPhase] = React.useState("bar"); // "bar" | "full"
  const [title, setTitle] = React.useState("");
  const [type, setType] = React.useState(defaultType === "event" ? "event" : "task");
  const [when, setWhen] = React.useState({ date: defaultDate || todayStr(), dueDate: "", dueTime: "", time: "", endTime: "", allDay: null });
  const [categoryId, setCategoryId] = React.useState("");
  const [priority, setPriority] = React.useState("");
  // Which panel is open: "date" | "category" | "priority" | null. One at a time —
  // every other button in the bar closes it (Jade).
  const [panel, setPanel] = React.useState(null);
  const [added, setAdded] = React.useState("");
  const [pastedLines, setPastedLines] = React.useState(null);
  // Subtasks typed in the bar (tasks only): [{ id, title }]. Empty rows are dropped on save.
  const [subs, setSubs] = React.useState([]);
  const subRefs = React.useRef({});
  const focusSubRef = React.useRef(null); // id of a row to focus once it renders
  const inputRef = React.useRef(null);
  const kbInset = useBrowserKeyboardInset();

  // Hold the bar still while an item is added. Clearing the title restarts the
  // keyboard's input connection, and on Android the keyboard can drop for a
  // split second and come back — the bar (which rides on it) dipped down and
  // popped back up each time Jade pressed Enter. For HOLD_MS after a send the
  // bar's bottom edge is pinned to the screen pixel it was at (anchored by
  // `top`, which a viewport resize doesn't move), then handed back to the
  // keyboard. If the keyboard really did close, the bar follows it then.
  const barRef = React.useRef(null);
  const holdRef = React.useRef(null); // { bottom, timer } while holding
  const kbInsetRef = React.useRef(kbInset);
  kbInsetRef.current = kbInset;
  const placeBar = React.useCallback(() => {
    const el = barRef.current;
    if (!el) return;
    const hold = holdRef.current;
    if (hold) { el.style.top = `${Math.round(hold.bottom - el.offsetHeight)}px`; el.style.bottom = "auto"; }
    else { el.style.top = "auto"; el.style.bottom = `${kbInsetRef.current}px`; }
  }, []); // reads only refs
  React.useLayoutEffect(placeBar); // every render: content height may have changed
  const holdPosition = () => {
    const el = barRef.current;
    if (!el) return;
    clearTimeout(holdRef.current?.timer);
    holdRef.current = {
      bottom: el.getBoundingClientRect().bottom,
      timer: setTimeout(() => { holdRef.current = null; placeBar(); }, HOLD_MS),
    };
  };
  React.useEffect(() => () => clearTimeout(holdRef.current?.timer), []);
  const isTask = type === "task";

  const focusTitle = React.useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.focus({ preventScroll: true });
    showKeyboardNow(); // not Keyboard.show(): that one waits 350ms (keyboardNow.js)
  }, []);

  // Open with the keyboard up.
  React.useEffect(() => {
    if (phase !== "bar") return;
    focusTitle();
    const t = setTimeout(focusTitle, 60); // the WebView sometimes ignores the first focus during the opening tap
    return () => clearTimeout(t);
  }, [phase, focusTitle]);

  // No Google autofill strip above the keyboard while the bar is open (Jade,
  // round 5): it appeared for every subtask row and pushed the bar around.
  React.useEffect(() => { setNativeAutofill(false); return () => setNativeAutofill(true); }, []);

  // Android back button: closes an open panel first, then the bar.
  // From the full form ("More"), back / x / a tap on the dim return to the bar
  // with everything typed there intact (Jade) — only saving closes it all.
  const panelRef = React.useRef(panel);
  panelRef.current = panel;
  const phaseRef = React.useRef(phase);
  phaseRef.current = phase;
  React.useEffect(() => pushBackHandler(() => {
    if (phaseRef.current === "full") { setPhase("bar"); return true; }
    if (panelRef.current) { setPanel(null); return true; }
    onClose(); return true;
  }), [onClose]);

  const category = categories.find(c => c.id === categoryId);
  const togglePanel = (name) => setPanel(p => (p === name ? null : name));

  // Subtasks are a panel like Category / Priority (Jade): only one is open, only
  // its button is highlighted. The rows are kept (just hidden) while it's closed.
  // If a row had focus when it closed, give focus back to the title so the
  // keyboard (and the bar riding on it) doesn't drop.
  const prevPanelRef = React.useRef(panel);
  React.useEffect(() => {
    const prev = prevPanelRef.current;
    prevPanelRef.current = panel;
    if (prev === "subtasks" && panel !== "subtasks" && panel !== "date" && phase === "bar"
        && document.activeElement !== inputRef.current) focusTitle();
  }, [panel, phase, focusTitle]);

  // Focus a just-added subtask row (after it has rendered).
  React.useEffect(() => {
    const id = focusSubRef.current;
    if (!id) return;
    focusSubRef.current = null;
    const el = subRefs.current[id];
    if (el) el.focus({ preventScroll: false });
  }, [subs]);

  const addSub = (afterId) => {
    holdPosition(); // moving focus to a new row can make the keyboard bounce too
    const row = { id: uid(), title: "" };
    focusSubRef.current = row.id;
    setSubs(prev => {
      if (!afterId) return [...prev, row];
      const i = prev.findIndex(x => x.id === afterId);
      return [...prev.slice(0, i + 1), row, ...prev.slice(i + 1)];
    });
  };
  const removeSub = (id) => {
    if (subs.length <= 1) setPanel(p => (p === "subtasks" ? null : p)); // last row gone: nothing left to show
    setSubs(prev => prev.filter(x => x.id !== id));
  };
  // The × on a subtask row (Jade, 2026-10-10: the keyboard closed on every
  // tap). Focus moves to a row that stays BEFORE the row goes, so a text field
  // keeps focus throughout and Android has no reason to drop the keyboard:
  // the row being typed in if it isn't the one removed, else the row above,
  // else the one below, else the title.
  const removeSubKeepTyping = (id, i) => {
    holdPosition();
    const focusedId = Object.keys(subRefs.current).find(k => subRefs.current[k] === document.activeElement);
    const keepId = focusedId && focusedId !== id ? focusedId : (subs[i - 1] || subs[i + 1] || {}).id;
    const el = keepId ? subRefs.current[keepId] : inputRef.current;
    if (el && document.activeElement !== el) el.focus({ preventScroll: true });
    removeSub(id);
  };
  const savedSubtasks = () => subs.map(x => ({ id: x.id, title: x.title.trim(), done: false })).filter(x => x.title);

  const addTitles = (titles) => {
    if (!titles.length) return;
    holdPosition();
    titles.forEach(t => onAdd(type, buildItemPayload({
      type, initialType: type, title: t,
      date: when.date, dueDate: isTask ? when.dueDate : "",
      dueTime: isTask ? (when.dueTime || "") : undefined,
      hasTime: !!when.time, time: when.time, endTime: when.time ? when.endTime : "", // end time is optional
      allDay: when.allDay ?? (!isTask && !when.time),
      categoryId,
      priority: isTask ? priority : "",
      // Subtasks belong to the one task being typed, not to every line of a pasted list.
      subtasks: isTask && titles.length === 1 ? savedSubtasks() : [],
    })));
    setAdded(titles.length === 1 ? `Added “${titles[0]}”` : `Added ${titles.length} ${isTask ? "tasks" : "events"}`);
    setTitle("");
    setSubs([]);
    setPanel(p => (p === "subtasks" ? null : p));
    // Only re-focus if focus actually left the title (a subtask row, a button).
    // Asking for the keyboard again while it's already up can bounce it.
    if (document.activeElement !== inputRef.current) focusTitle();
  };

  const send = () => {
    const t = title.trim();
    if (!t) return;
    setPanel(null);
    addTitles([t]);
  };

  const onPaste = (e) => {
    const lines = splitPastedLines(e.clipboardData?.getData("text") || "");
    if (lines.length < 2) return;
    e.preventDefault();
    setPanel(null);
    setPastedLines(lines);
  };

  // The keyboard is closed first and the date panel drawn a couple of frames
  // later (Jade, 2026-10-11): drawing it in the same moment held the closing
  // keyboard up until the panel was ready.
  const openDate = () => {
    try { inputRef.current && inputRef.current.blur(); } catch (_) {}
    if (isNative) Keyboard.hide().catch(() => {});
    requestAnimationFrame(() => requestAnimationFrame(() => setPanel("date")));
  };

  const openMore = () => {
    try { inputRef.current && inputRef.current.blur(); } catch (_) {}
    if (isNative) Keyboard.hide().catch(() => {});
    setPhase("full");
  };

  if (phase === "full") {
    return (
      <Modal onClose={() => setPhase("bar")} blur={false} dim={0.5} animate={false}>
        <ItemPanel
          mode="add"
          initialType={type}
          defaultDate={when.date}
          defaultDueDate={isTask ? when.dueDate : ""}
          defaultTime={when.time}
          defaultEndTime={when.time ? when.endTime : ""}
          defaultDueTime={isTask ? when.dueTime : ""}
          defaultAllDay={when.allDay}
          defaultTitle={title}
          defaultCategoryId={categoryId}
          defaultPriority={isTask ? priority : ""}
          defaultSubtasks={isTask ? savedSubtasks() : []}
          categories={categories} setCategories={setCategories} savedColors={savedColors} addSavedColor={addSavedColor}
          onClose={() => setPhase("bar")}
          onSubmit={(t, payload) => { onAdd(t, payload); onClose(); }}
        />
      </Modal>
    );
  }

  const canSend = !!title.trim();
  const priColor = priority ? PRIORITY_COLORS[priority] : null;

  return portal(
    <div style={{ position: "fixed", inset: 0, zIndex: 100000, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
      <style>{QA_CSS}</style>
      {/* Dim backdrop — tap to close */}
      <div onClick={onClose} style={{ position: "absolute", inset: 0, background: "rgba(60,30,50,0.32)" }} />

      <div
        ref={barRef}
        role="dialog" aria-label="Quick add"
        style={{
          // top / bottom are set by placeBar() (see the hold above), not here.
          position: "absolute", left: 0, right: 0,
          // Hidden (not unmounted) while the date panel is open, so typed text,
          // subtasks and the bar's position all survive (Jade, round 5).
          visibility: panel === "date" ? "hidden" : "visible",
          background: "var(--c-bg-card)",
          borderRadius: "22px 22px 0 0",
          boxShadow: "0 -6px 28px rgba(60,30,50,0.16)",
          padding: "14px 14px calc(10px + var(--sab, 0px))",
        }}>

        {/* "Add N tasks?" after pasting a list */}
        {pastedLines && (
          <div style={{ background: "var(--c-bg-active)", borderRadius: 14, padding: "10px 12px", marginBottom: 10 }}>
            <p style={{ margin: "0 0 2px", fontSize: 14, fontWeight: 700, color: "var(--c-text)" }}>
              Add {pastedLines.length} {isTask ? "tasks" : "events"}?
            </p>
            <p style={{ margin: "0 0 8px", fontSize: 12, color: "var(--c-text-soft)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {pastedLines.slice(0, 3).join(" · ")}{pastedLines.length > 3 ? ` · +${pastedLines.length - 3} more` : ""}
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <button onMouseDown={keepFocus} onClick={() => setPastedLines(null)}
                style={{ ...pill(false), flex: 1, justifyContent: "center", background: "var(--c-bg-card)" }}>Cancel</button>
              <button onMouseDown={keepFocus} onClick={() => { setTitle(t => [t.trim(), ...pastedLines].filter(Boolean).join(" ")); setPastedLines(null); }}
                style={{ ...pill(false), flex: 1, justifyContent: "center", background: "var(--c-bg-card)", color: "var(--c-text-accent)" }}>Add as 1</button>
              <button onMouseDown={keepFocus} onClick={() => { const l = pastedLines; setPastedLines(null); addTitles(l); }}
                style={{ ...pill(false), flex: 1, justifyContent: "center", border: "none", background: GRAD, color: "var(--c-on-primary)" }}>Add {pastedLines.length}</button>
            </div>
          </div>
        )}

        {/* "✓ Added …" — the bar stays open, so this is the only sign it worked */}
        {added && !pastedLines && (
          <p style={{ margin: "0 0 8px 4px", fontSize: 12.5, fontWeight: 600, color: "var(--c-text-accent)", display: "flex", alignItems: "center", gap: 5, overflow: "hidden", whiteSpace: "nowrap" }}>
            <CheckCircle size={14} style={{ flexShrink: 0 }} />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{added}</span>
          </p>
        )}

        {/* Title + Send */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <input
            ref={inputRef}
            autoComplete="off"
            className="qa-plain"
            enterKeyHint="send"
            placeholder={isTask ? "Task" : "Event"}
            value={title}
            onChange={e => setTitle(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); send(); } }}
            onPaste={onPaste}
            style={{
              flex: 1, minWidth: 0, height: 46, boxSizing: "border-box",
              border: "none", outline: "none", boxShadow: "none",
              borderRadius: 14, padding: "0 14px",
              background: "var(--c-bg-active)", color: "var(--c-text)",
              fontFamily: "inherit", fontSize: 16, fontWeight: 600,
            }}
          />
          <button
            aria-label="Add" onMouseDown={keepFocus} onClick={send} disabled={!canSend}
            style={{
              width: 46, height: 46, borderRadius: "50%", border: "none", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              background: canSend ? GRAD : "var(--c-border-mid)",
              color: "var(--c-on-primary)", cursor: canSend ? "pointer" : "default",
              transition: "background 0.15s",
            }}>
            <ArrowUp size={22} strokeWidth={2.6} />
          </button>
        </div>

        {/* Subtasks — the bar grows a row per subtask, scrolling past five */}
        {isTask && panel === "subtasks" && subs.length > 0 && (
          <div style={{ maxHeight: 5 * SUB_ROW_H, overflowY: "auto", overscrollBehavior: "contain", marginTop: 6 }}>
            {subs.map((x, i) => (
              <div key={x.id} style={{ display: "flex", alignItems: "center", gap: 10, height: SUB_ROW_H, padding: "0 4px 0 10px" }}>
                <Circle size={18} strokeWidth={1.8} color="var(--c-text-soft)" style={{ flexShrink: 0 }} />
                <input
                  ref={el => { if (el) subRefs.current[x.id] = el; else delete subRefs.current[x.id]; }}
                  autoComplete="off" enterKeyHint="enter" className="qa-plain"
                  placeholder="Add a subtask"
                  value={x.title}
                  onFocus={() => setPanel("subtasks")}
                  onChange={e => { const v = e.target.value; setSubs(prev => prev.map(y => y.id === x.id ? { ...y, title: v } : y)); }}
                  onKeyDown={e => {
                    if (e.key === "Enter") { e.preventDefault(); if (x.title.trim()) addSub(x.id); }
                    else if (e.key === "Backspace" && !x.title) {
                      // Backspace on an empty row removes it and steps back up.
                      e.preventDefault();
                      const prevRow = subs[i - 1];
                      holdPosition();
                      removeSub(x.id);
                      const el = prevRow ? subRefs.current[prevRow.id] : inputRef.current;
                      if (el) el.focus({ preventScroll: true });
                    }
                  }}
                  style={{ flex: 1, minWidth: 0, border: "none", outline: "none", boxShadow: "none", background: "transparent", fontFamily: "inherit", fontSize: 15, fontWeight: 600, color: "var(--c-text)", padding: 0 }}
                />
                <button aria-label="Remove subtask" tabIndex={-1} onMouseDown={keepFocus} onClick={() => removeSubKeepTyping(x.id, i)}
                  style={{ border: "none", background: "transparent", color: "var(--c-text-soft)", padding: 6, cursor: "pointer", display: "flex" }}>
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Category / Priority: a vertical list card above the bar (Jade, round 4 —
            like Todoist's menu). Scrolls past MENU_ROWS rows. */}
        {panel === "category" && (
          <ChoiceMenu label="Category">
            <MenuRow selected={!categoryId} onPick={() => { setCategoryId(""); setPanel(null); }}
              icon={<Tag size={18} strokeWidth={1.9} />} label="No category" />
            {categories.map(c => (
              <MenuRow key={c.id} selected={c.id === categoryId} onPick={() => { setCategoryId(c.id); setPanel(null); }}
                icon={<CategoryIcon cat={c} size={18} color={categoryInk(c.color)} />} label={c.title} />
            ))}
            {!categories.length && (
              <p style={{ margin: 0, padding: "8px 14px 10px", fontSize: 12.5, color: "var(--c-text-soft)" }}>No categories yet. Add one in More</p>
            )}
          </ChoiceMenu>
        )}
        {panel === "priority" && isTask && (
          <ChoiceMenu label="Priority">
            <MenuRow selected={!priority} onPick={() => { setPriority(""); setPanel(null); }}
              icon={<Flag size={18} strokeWidth={1.9} />} label="None" />
            {TASK_PRIORITIES.map(p => (
              <MenuRow key={p.id} selected={priority === p.id} onPick={() => { setPriority(p.id); setPanel(null); }}
                icon={<Flag size={18} strokeWidth={1.9} color={PRIORITY_COLORS[p.id]} fill={PRIORITY_COLORS[p.id]} />} label={p.label} />
            ))}
          </ChoiceMenu>
        )}

        {/* Date · Category · Priority · Subtasks — icons only (Jade); Task/Event keeps its word; More */}
        <div style={{ display: "flex", alignItems: "center", gap: 2, marginTop: 8 }}>
          <button onMouseDown={keepFocus} onClick={openDate} title={quickDateLabel(when.date)}
            aria-label={`Date: ${quickDateLabel(when.date)}`}
            style={iconBtn(panel === "date")}>
            {/* Calendar icon with the start day's number inside it */}
            <span style={{ position: "relative", display: "flex" }}>
              <Calendar size={22} strokeWidth={1.8} />
              <span style={{ position: "absolute", left: 0, right: 0, top: 8, textAlign: "center", fontSize: 9.5, fontWeight: 800, lineHeight: 1 }}>
                {Number(when.date.slice(8, 10)) || ""}
              </span>
            </span>
            {(when.time || (isTask && when.dueTime)) && (
              <Clock size={11} strokeWidth={2.6} style={{ position: "absolute", right: 3, bottom: 3, background: "var(--c-bg-card)", borderRadius: "50%" }} />
            )}
          </button>

          <button onMouseDown={keepFocus} onClick={() => togglePanel("category")}
            title="Category" aria-label={category ? `Category: ${category.title}` : "Category"}
            style={{ ...iconBtn(panel === "category"), ...(category && panel === "category" ? { background: category.color + "26", color: categoryInk(category.color) } : {}) }}>
            {category ? <CategoryIcon cat={category} size={20} color={categoryInk(category.color)} /> : <Tag size={20} strokeWidth={1.9} />}
          </button>

          {isTask && (
            <button onMouseDown={keepFocus} onClick={() => togglePanel("priority")}
              title="Priority" aria-label={priority ? `Priority: ${priority}` : "Priority"}
              style={{ ...iconBtn(panel === "priority"), ...(priColor && panel === "priority" ? { background: priColor + "26" } : {}) }}>
              <Flag size={20} strokeWidth={1.9} color={priColor || "currentColor"} fill={priColor || "none"} />
            </button>
          )}

          {isTask && (
            <button onMouseDown={keepFocus} onClick={() => {
                if (panel === "subtasks") { setPanel(null); return; }
                setPanel("subtasks");
                if (!subs.length) addSub();
              }}
              title="Subtasks" aria-label="Subtasks"
              style={iconBtn(panel === "subtasks")}>
              <ListTree size={20} strokeWidth={1.9} />
            </button>
          )}

          <button onMouseDown={keepFocus} onClick={() => { setPanel(null); setType(t => t === "task" ? "event" : "task"); }}
            aria-label={isTask ? "Task (tap for event)" : "Event (tap for task)"}
            style={{ ...textBtn, color: "var(--c-text-accent)" }}>
            {isTask ? <CheckSquare size={15} /> : <CalendarDays size={15} />}
            {isTask ? "Task" : "Event"}
          </button>

          <div style={{ flex: 1 }} />
          <button onClick={openMore}
            style={{ ...textBtn, color: "var(--c-text-accent)", textDecoration: "underline", textUnderlineOffset: 3, padding: "0 2px 0 6px" }}>
            More
          </button>
        </div>
      </div>

      {panel === "date" && (
        <DatePanel
          type={type}
          initial={when}
          weekStart={weekStart}
          onCancel={() => { setPanel(null); setTimeout(focusTitle, 50); }}
          onDone={(next) => { setWhen(next); setPanel(null); setTimeout(focusTitle, 50); }}
        />
      )}
    </div>
  );
}
